import React, { Component } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Alert,
} from '@mui/material'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ImageSelector from './ImageSelector'

const MAX_IMAGES = 20

// Resized variants live beside the original as `<size>_<filename>`. Returns null instead of
// throwing on a malformed media object - ImageSelectDialog does the same splice unguarded
// and blanks the whole modal when a document is missing its url.
export const mediaThumbUrl = (media, size) => {
  if (!media || typeof media.url !== 'string') return null
  const parts = media.url.split('/')
  const file = parts.pop()
  const prefix = media.sizes && media.sizes.length > 1 ? (size || media.sizes[1]) + '_' : ''
  return parts.join('/') + '/' + prefix + file
}

const aspectRatioOf = (media) => {
  if (!media || !media.metadata || !media.metadata.width || !media.metadata.height) return null
  return media.metadata.width / media.metadata.height
}

// Picks several images from the media library. The value is an ordered array of whole media
// objects, the same shape the single image fields store.
class MultiImageSelectDialog extends Component {
  constructor(props) {
    super(props)
    // Only the in-dialog draft is state. Nothing is mirrored from props, so there is no
    // stale copy to go out of sync.
    this.state = {
      open: false,
      draftIds: [],
    }
  }

  value = () => (Array.isArray(this.props.value) ? this.props.value.filter(Boolean) : [])

  library = () => (Array.isArray(this.props.images) ? this.props.images : [])

  openDialog = () => {
    this.setState({
      open: true,
      draftIds: this.value()
        .map((m) => m && m.id)
        .filter(Boolean),
    })
  }

  closeDialog = () => this.setState({ open: false })

  // ImageSelector keeps its single select contract: { target: { value: id } }
  handleToggle = (e) => {
    const id = e.target.value
    const draftIds = this.state.draftIds.slice()
    const i = draftIds.indexOf(id)
    if (i > -1) {
      draftIds.splice(i, 1)
    } else {
      if (draftIds.length >= MAX_IMAGES) return
      draftIds.push(id) // click order defines gallery order
    }
    this.setState({ draftIds })
  }

  confirm = () => {
    const library = this.library()
    const previous = this.value()
    const next = this.state.draftIds
      // Fall back to the stored object so an image whose media document was deleted is not
      // silently dropped just because the dialog was reopened.
      .map((id) => library.find((m) => m.id === id) || previous.find((m) => m && m.id === id))
      .filter(Boolean)
    this.props.onChange(next)
    this.closeDialog()
  }

  removeAt = (index) => {
    const next = this.value().slice()
    next.splice(index, 1)
    this.props.onChange(next)
  }

  moveBy = (index, delta) => {
    const target = index + delta
    const next = this.value().slice()
    if (target < 0 || target >= next.length) return
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item)
    this.props.onChange(next)
  }

  // Gallery images are secondary content, so a small or unverifiable aspect ratio is worth a
  // warning but must not block. The single image field keeps its blocking check.
  aspectWarnings = () => {
    const min = this.props.minaspectratio
    if (!min) return []
    const library = this.library()
    return this.state.draftIds
      .map((id) => library.find((m) => m.id === id))
      .filter(Boolean)
      .filter((m) => {
        const ratio = aspectRatioOf(m)
        return ratio === null || ratio < min
      })
      .map((m) => m.name || m.originalName || m.id)
  }

  renderStrip = () => {
    const value = this.value()
    if (!value.length) {
      return (
        <Typography variant='body2' style={{ color: 'gray' }}>
          Ei valittuja kuvia.
        </Typography>
      )
    }
    return (
      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {value.map((media, index) => {
          const src = mediaThumbUrl(media, 150)
          return (
            <Box
              key={(media && media.id) || index}
              style={{
                position: 'relative',
                width: 110,
                border: '1px solid #ddd',
                borderRadius: 4,
                padding: 2,
              }}
            >
              {src ? (
                <img
                  src={src}
                  alt={(media && media.originalName) || ''}
                  style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Box style={{ height: 80, background: '#f2f2f2' }} />
              )}
              <IconButton
                size='small'
                aria-label='Poista kuva'
                disabled={this.props.disabled}
                onClick={() => this.removeAt(index)}
                style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(255,255,255,0.85)' }}
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <IconButton
                  size='small'
                  aria-label='Siirrä vasemmalle'
                  disabled={this.props.disabled || index === 0}
                  onClick={() => this.moveBy(index, -1)}
                >
                  <ChevronLeftIcon fontSize='inherit' />
                </IconButton>
                <Typography variant='caption' style={{ color: 'gray' }}>
                  {index + 1}
                </Typography>
                <IconButton
                  size='small'
                  aria-label='Siirrä oikealle'
                  disabled={this.props.disabled || index === value.length - 1}
                  onClick={() => this.moveBy(index, 1)}
                >
                  <ChevronRightIcon fontSize='inherit' />
                </IconButton>
              </Box>
            </Box>
          )
        })}
      </Box>
    )
  }

  render() {
    const warnings = this.state.open ? this.aspectWarnings() : []
    const mainImage = this.props.mainImage
    const mainMissing =
      mainImage && mainImage.id && !this.value().some((m) => m && m.id === mainImage.id)

    return (
      <>
        <Box style={{ marginTop: 8, marginBottom: 8 }}>
          <Typography variant='caption' component='div' style={{ color: 'gray' }} gutterBottom>
            {this.props.label}
          </Typography>
          {this.renderStrip()}
          <Button
            size='small'
            startIcon={<AddPhotoAlternateIcon />}
            disabled={this.props.disabled}
            onClick={this.openDialog}
            style={{ marginTop: 8 }}
          >
            Valitse kuvia
          </Button>
          {mainMissing ? (
            <Typography variant='caption' component='div' style={{ color: 'gray' }}>
              Huom: verkkokaupan tuotekuva ei ole galleriassa.
            </Typography>
          ) : null}
        </Box>

        <Dialog
          fullWidth
          maxWidth='sm'
          open={this.state.open}
          onClose={this.closeDialog}
          aria-labelledby='multi-image-dialog-title'
          style={{ zIndex: '9000' }}
        >
          <DialogTitle id='multi-image-dialog-title'>
            Valitse kuvat ({this.state.draftIds.length}/{MAX_IMAGES})
          </DialogTitle>
          <DialogContent>
            {warnings.length ? (
              <Alert severity='warning' style={{ marginBottom: 8 }}>
                Näiden kuvien kuvasuhdetta ei voitu vahvistaa tai se on suositeltua pienempi:{' '}
                {warnings.join(', ')}
              </Alert>
            ) : null}
            <ImageSelector
              images={this.library()}
              multiple
              selectedIds={this.state.draftIds}
              onSelect={this.handleToggle}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog} color='inherit'>
              Peruuta
            </Button>
            <Button onClick={this.confirm} color='primary' variant='contained'>
              Valmis
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }
}

export default MultiImageSelectDialog
